import { merge } from 'lodash-es';
import { isPlural, isTranslatable } from './format';

type TextTreeObject = object;

export function mergeTexts<TextTree extends TextTreeObject>(
  ...textTrees: (TextTree | undefined | null)[]
): TextTree {
  return merge({}, ...textTrees.filter(isTextTree)) as TextTree;
}

export function withTextKeys<TextTree>(textTree: TextTree): TextTree {
  return addTextKeys(textTree) as TextTree;
}

export const removePluralOverrides = <TextTree>(
  textTree: TextTree
): TextTree | undefined => {
  return removePluralOverridesFromTree(textTree) as TextTree | undefined;
};

function addTextKeys(value: unknown, path?: string): unknown {
  if (!isTextTree(value)) {
    return value;
  }

  if (isTranslatable(value)) {
    return {
      ...value,
      key: path,
    };
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      addTextKeys(child, path ? `${path}.${key}` : key),
    ])
  );
}

function removePluralOverridesFromTree(value: unknown): unknown {
  if (!isTextTree(value)) {
    return undefined;
  }

  if (isTranslatable(value)) {
    return isPlural(value.message)
      ? {
          ...value,
          message: { ...value.message, zero: undefined, one: undefined },
        }
      : { ...value };
  }

  const entries = Object.entries(value)
    .map(([key, child]) => [key, removePluralOverridesFromTree(child)] as const)
    .filter(([, child]) => child !== undefined);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function isTextTree(value: unknown): value is TextTreeObject {
  return typeof value === 'object' && value !== null;
}
